import React, { useMemo } from 'react';
import { Progress, Tooltip } from 'antd';
import { ExclamationCircleOutlined, InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import useGetTokenUsage from '../hooks/useGetTokenUsage';
import {
  OverLimitMessage,
  TokenAlert,
  TokenUsageSkeleton,
  TokenUsageWrapper,
  UsageText,
} from './styled';
import { checkIsAdminUser, checkIsInternalUser } from '@src/globalStore/profileStore';
import { short, full } from '@src/utils/formatters';
import { useContactFormStore } from '@pulse/nav';

const { setShowContactDrawer, setFormValues } = useContactFormStore.getState();

// Map color code to text colors
const getTextColor = (color: string) => {
  switch (color) {
    case 'green':
      return '#52c41a';
    case 'yellow':
      return '#faad14';
    case 'red':
      return '#ff4d4f';
    default:
      return '#000000';
  }
};

const TokenUsage: React.FC = () => {
  const { isFetching, isError, isOver, usedPercentage, colorCode, data } = useGetTokenUsage();
  const isInternal = checkIsInternalUser();
  const isAdmin = checkIsAdminUser();

  const infoText = useMemo(() => {
    let baseText =
      'Conviva is continuously working to improve token efficiency. All tokens are reset monthly (UTC).';
    if (isInternal) {
      baseText += ' Internal usage is not counted toward customer monthly token limit.';
    }
    return baseText;
  }, [isInternal]);

  // Extract token data from the API response
  const currentTokens = data?.current_tokens || 0;
  const monthlyLimit = data?.monthly_limit || 0;

  const handleContactSupport = (e) => {
    e.preventDefault();
    setFormValues({
      subject: 'Nexa - Usage Limit Reached',
      summary:
        'I have reached my monthly usage limit under the Nexa program and need assistance to continue using the service.',
      priority: 'P2',
      feature: 'Nexa',
    });
    setShowContactDrawer(true);
  };

  if (isFetching) {
    return (
      <TokenUsageWrapper>
        <TokenUsageSkeleton paragraph={false} active />
      </TokenUsageWrapper>
    );
  }

  if (isError) {
    return (
      <TokenUsageWrapper>
        <TokenAlert
          message="Unable to load usage information"
          type="warning"
          showIcon
          icon={<WarningOutlined />}
        />
      </TokenUsageWrapper>
    );
  }

  return (
    <TokenUsageWrapper>
      {isOver ? (
        <OverLimitMessage>
          <TokenAlert
            message={
              isAdmin ? (
                <span>
                  You&apos;ve reached your monthly usage limit under the Nexa program. Please{' '}
                  <a
                    href="#"
                    onClick={handleContactSupport}
                    style={{ textDecoration: 'underline' }}
                  >
                    contact support team
                  </a>{' '}
                  to continue.
                </span>
              ) : (
                <span>
                  You&apos;ve reached your monthly usage limit under the Nexa program. Please ask
                  your admin to contact support team to continue.
                </span>
              )
            }
            type="error"
            showIcon
            icon={<ExclamationCircleOutlined />}
          />
        </OverLimitMessage>
      ) : (
        <UsageText>
          <Tooltip title={`${usedPercentage}% used`}>
            <Progress
              percent={usedPercentage}
              size="small"
              showInfo={false}
              strokeColor={getTextColor(colorCode)}
              trailColor={'rgba(0, 0, 0, 0.13)'}
              steps={10}
            />
          </Tooltip>
          <Tooltip title={`${full(currentTokens)} / ${full(monthlyLimit)} tokens`}>
            <div style={{ color: '#888', fontSize: '12px' }}>
              {short(currentTokens)} of {short(monthlyLimit)} monthly tokens used.
            </div>
          </Tooltip>
        </UsageText>
      )}
      <div>
        <Tooltip title={infoText}>
          <InfoCircleOutlined style={{ color: '#888', fontSize: 12 }} />
        </Tooltip>
      </div>
    </TokenUsageWrapper>
  );
};

export default TokenUsage;
